import { useToggle } from "@react-hookz/web";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import type { topicsCardTopicSearchEdge$key } from "scmp-app/queries/__generated__/topicsCardTopicSearchEdge.graphql";

import IconArrow from "./icon-arrow.svg";
import { TopicsCardItem } from "./item";
import { Container, Title, Toggle } from "./styles";

type Props = {
  className?: string;
  reference: topicsCardTopicSearchEdge$key;
};

export const TopicsCard: FunctionComponent<Props> = ({ className, reference }) => {
  const topics = useFragment(
    graphql`
      fragment topicsCardTopicSearchEdge on TopicSearchEdge @relay(plural: true) {
        node {
          content {
            entityId
            ...itemTopicsCardItemTopic
          }
        }
      }
    `,
    reference,
  );

  const [isExpand, toggleIsExpand] = useToggle();

  const renderItems = useCallback(() => {
    const filteredItems = topics
      .filter(({ node }) => notEmpty(node))
      .filter((_, index) => isExpand || index === 0);
    return filteredItems?.map(
      ({ node }) =>
        notEmpty(node.content) && (
          <TopicsCardItem key={node.content.entityId} reference={node.content} />
        ),
    );
  }, [isExpand, topics]);

  if (!notEmpty(topics)) return null;
  return (
    <Container className={className}>
      <Title>Topics</Title>

      {renderItems()}

      {topics.length > 1 && (
        <Toggle $isExpand={isExpand} onClick={toggleIsExpand}>
          <span>{isExpand ? "Show less" : "Show all topics"}</span>
          <IconArrow />
        </Toggle>
      )}
    </Container>
  );
};

TopicsCard.displayName = "TopicsCard";
